import React, { FC, ReactNode, createContext, useContext, useEffect, useState } from 'react'
import { IntlProvider } from 'react-intl'

import storage from '../../utils/storage'

type LocalizationContextType = {
    locale: string
    setLocale: (locale: string) => void
}

export type Locale = 'en' | 'de' | 'es' | 'sv' | 'fi' | 'no' | 'fr' | 'nl'

export type Messages = { [key: string]: string }

export const AVAILABLE_LANGUAGES_LOCAL: { [key: string]: string } = {
    de: 'Deutsch',
    en: 'English',
    es: 'Español',
    nl: 'Nederlands',
    fr: 'Français',
    no: 'Norsk',
    fi: 'Suomen Kieli',
    sv: 'Svenska',
}

export const AVAILABLE_LANGUAGES = ['de', 'en', 'es', 'fi', 'fr', 'no', 'sv', 'nl']

const savedLocale = storage.getItem<string>('userLocale')
const browserLocal = navigator.language.split(/[-_]/)[0]

const initialLocale =
    (savedLocale && AVAILABLE_LANGUAGES.includes(savedLocale) ? savedLocale : undefined) ||
    (AVAILABLE_LANGUAGES.includes(browserLocal) ? browserLocal : undefined) ||
    'en'

const LocalizationContext = createContext<LocalizationContextType>({ locale: initialLocale } as LocalizationContextType)

const loadMessagesByLocale = (locale: string) => {
    switch (locale) {
        case 'de':
            return import('../../..//messages/de.json')
        case 'es':
            return import('../../..//messages/es.json')
        case 'sv':
            return import('../../..//messages/sv.json')
        case 'fi':
            return import('../../..//messages/fi.json')
        case 'fr':
            return import('../../..//messages/fr.json')
        case 'nl':
            return import('../../..//messages/nl.json')
        case 'no':
            return import('../../../messages/no.json')
        default:
            return import('../../..//messages/en.json')
    }
}

const use = (promise: any) => {
    if (promise.status === 'fulfilled') {
        return promise.value
    } else if (promise.status === 'rejected') {
        throw promise.reason
    } else if (promise.status === 'pending') {
        throw promise
    } else {
        promise.status = 'pending'
        promise.then(
            (result: string) => {
                promise.status = 'fulfilled'
                promise.value = result
            },
            (reason: string) => {
                promise.status = 'rejected'
                promise.reason = reason
            }
        )
        throw promise
    }
}

const LocalizationProvider: FC<{ children: ReactNode }> = ({ children }) => {
    const [locale, setLocale] = useState<string>(initialLocale)
    const messages = use(loadMessagesByLocale(locale))

    useEffect(() => {
        storage.setItem('userLocale', locale)
    }, [locale])

    return (
        <LocalizationContext.Provider value={{ locale, setLocale }}>
            <IntlProvider locale={locale} messages={messages}>
                {children}
            </IntlProvider>
        </LocalizationContext.Provider>
    )
}

const useLocalizationContext = () => {
    const context = useContext(LocalizationContext)
    return context
}

export { LocalizationContext, LocalizationProvider, useLocalizationContext }
